// Tour preview mode.
//
// When the snippet loads with `?steppr_preview=<tourId>&steppr_preview_token=<t>`,
// we bypass the normal session flow and boot preview mode instead. A subtle
// toolbar at the top of the page makes it clear the author is in preview
// (not a live tour), and the tour plays against the current draft regardless
// of targeting/trigger rules.
//
// Preview is read-only: no MAU counting, no analytics, no side effects.

import type { Theme, Tour, TourAction } from './types';
import { DEFAULT_THEME } from './types';
import { startTour, applyTheme } from './runtime';

type PreviewOptions = {
  apiUrl: string;
  projectKey: string;
  tourId: string;
  token: string;
};

type PreviewResponse = {
  ok: boolean;
  tour?: Tour;
  theme?: Theme;
};

// Same shadow-DOM gotcha as the tour runtime: the host element gets
// `style="all: initial"` set inline on the host node, which inline-wins over
// any :host rule. So we don't bother trying to set font-family on :host -
// instead we apply it via the universal selector to every descendant, which
// renders inside the shadow boundary and doesn't fight the inline style.
// Without this, browsers drop back to their default serif (Times New Roman
// on most platforms) for the toolbar text and buttons.
const TOOLBAR_CSS = `
:host {
  font-size: 12px;
  line-height: 1.4;
  color: #f8fafc;
}
* {
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}
button { font: inherit; }
.bar {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2147483646;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px 8px 14px;
  background: rgba(15, 23, 42, 0.96);
  color: #f8fafc;
  border-radius: 999px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.35);
  font-size: 12px;
  line-height: 1.4;
  max-width: calc(100vw - 32px);
}
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #38bdf8;
  animation: preview-pulse 1.6s ease-out infinite;
}
.label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}
.tag {
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(248, 250, 252, 0.7);
  padding: 2px 6px;
  border-radius: 999px;
  border: 1px solid rgba(248, 250, 252, 0.2);
}
button {
  font: inherit;
  background: transparent;
  color: inherit;
  border: 1px solid rgba(248, 250, 252, 0.25);
  padding: 3px 10px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 120ms ease;
}
button:hover { background: rgba(248, 250, 252, 0.1); }
button:focus-visible { outline: 2px solid #38bdf8; outline-offset: 2px; }
@keyframes preview-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
`;

type PreviewMessage =
  | {
      type: 'steppr:preview:text-edit';
      stepId: string;
      field: 'title' | 'body';
      value: string;
    };

type PreviewState = {
  mounted: boolean;
  tour: Tour | null;
  opener: Window | null;
  openerOrigin: string;
  callbacks: {
    sendTourEvent: (action: TourAction, tourId: string, stepId?: string) => void;
    onTextEdit?: (stepId: string, field: 'title' | 'body', value: string) => void;
  };
};

const state: PreviewState = {
  mounted: false,
  tour: null,
  opener: null,
  openerOrigin: '*',
  callbacks: {
    // No-op sink: preview mode never reports analytics. Keeps the runtime
    // rendering path simple (it always calls fireTourEvent; we absorb them).
    sendTourEvent: () => {},
  },
};

function postToOpener(msg: PreviewMessage): void {
  if (!state.opener) return;
  try {
    state.opener.postMessage(msg, state.openerOrigin);
  } catch {
    // Opener navigated away or was closed - nothing to do.
  }
}

function mountToolbar(tourName: string, onReplay: () => void, onExit: () => void): void {
  const host = document.createElement('div');
  host.id = '__steppr_preview_toolbar__';
  host.style.all = 'initial';
  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = TOOLBAR_CSS;
  shadow.appendChild(style);

  const bar = document.createElement('div');
  bar.className = 'bar';

  const dot = document.createElement('span');
  dot.className = 'dot';
  bar.appendChild(dot);

  const tag = document.createElement('span');
  tag.className = 'tag';
  tag.textContent = 'Preview';
  bar.appendChild(tag);

  const label = document.createElement('span');
  label.className = 'label';
  label.textContent = tourName;
  label.title = 'Targeting rules are bypassed in preview';
  bar.appendChild(label);

  const replay = document.createElement('button');
  replay.type = 'button';
  replay.textContent = 'Replay';
  replay.addEventListener('click', onReplay);
  bar.appendChild(replay);

  const exit = document.createElement('button');
  exit.type = 'button';
  exit.textContent = 'Exit';
  exit.addEventListener('click', onExit);
  bar.appendChild(exit);

  shadow.appendChild(bar);
  document.body.appendChild(host);
}

async function fetchPreview(opts: PreviewOptions): Promise<PreviewResponse | null> {
  try {
    const res = await fetch(`${opts.apiUrl}/v1/preview/${encodeURIComponent(opts.tourId)}`, {
      method: 'GET',
      headers: {
        'X-Steppr-Key': opts.projectKey,
        'X-Steppr-Preview-Token': opts.token,
      },
      credentials: 'omit',
      mode: 'cors',
    });
    if (!res.ok) return null;
    return (await res.json().catch(() => null)) as PreviewResponse | null;
  } catch {
    return null;
  }
}

// Force-start the tour from step 1. Reused by the Replay button. Because
// preview bypasses the normal shown-tour dedup, we also clear any lingering
// state via a hard reload-of-runtime approach: the startTour call guards on
// "already running"; we ignore that by scheduling after a tick so the close
// side-effects complete.
function playPreviewTour(tour: Tour): void {
  startTour(tour, state.callbacks);
}

export function bootPreview(opts: PreviewOptions): void {
  if (state.mounted) return;
  state.mounted = true;

  // Capture the dashboard window + its origin so inline text edits can
  // postMessage back upstream. Same handshake the picker uses; reads the
  // origin from the URL the dashboard appended on window.open.
  state.opener = typeof window !== 'undefined' ? window.opener : null;
  try {
    const params = new URLSearchParams(window.location.search);
    state.openerOrigin = params.get('steppr_opener') ?? '*';
  } catch {
    state.openerOrigin = '*';
  }
  // Inline editing shows raw markdown source in the card, so only turn it
  // on when a dashboard window opened us and can receive the edits. A
  // preview link opened on its own (agent / API generated, or pasted into a
  // fresh tab) renders exactly what end users will see.
  if (state.opener) {
    state.callbacks.onTextEdit = (stepId, field, value) => {
      postToOpener({ type: 'steppr:preview:text-edit', stepId, field, value });
    };
  }

  void (async () => {
    const res = await fetchPreview(opts);
    if (!res?.tour) {
      showFatal('Preview link expired or invalid. Generate a new one from the dashboard.');
      return;
    }
    state.tour = res.tour;

    // Apply the workspace's live theme so the preview matches what end users
    // would actually see. Falls back to defaults if the API didn't ship one
    // (older preview-token endpoints, network blip, etc.).
    applyTheme({ ...DEFAULT_THEME, ...(res.theme ?? {}) });

    mountToolbar(
      res.tour.name,
      () => {
        if (state.tour) playPreviewTour(state.tour);
      },
      () => {
        try {
          window.close();
        } catch {
          // Browsers may block close() in some cases - fall back to hiding our
          // chrome so the author knows we've bowed out.
          document.getElementById('__steppr_preview_toolbar__')?.remove();
        }
      },
    );

    playPreviewTour(res.tour);
  })();
}

function showFatal(message: string): void {
  const host = document.createElement('div');
  host.id = '__steppr_preview_toolbar__';
  host.style.all = 'initial';
  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = TOOLBAR_CSS;
  shadow.appendChild(style);
  const bar = document.createElement('div');
  bar.className = 'bar';
  bar.style.backgroundColor = 'rgba(127, 29, 29, 0.96)';
  bar.textContent = message;
  shadow.appendChild(bar);
  document.body.appendChild(host);
}
